import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Plot from 'react-plotly.js';
import Block from './Block';
import { markdownTableComponents } from './TextBubble';

/**
 * Renders a `deep_analysis_result` block: the RLM's narrative answer plus an
 * optional markdown table, an optional Plotly chart, and a collapsible "Show
 * code" panel exposing the final pandas snippet.
 */
const DeepAnalysisResult = ({ blockData = {} }) => {
  const { question, narrative, table_md, chart_spec_json, code } = blockData;
  const [showCode, setShowCode] = useState(false);

  const chartSpec = useMemo(() => {
    if (!chart_spec_json) return null;
    try {
      return JSON.parse(chart_spec_json);
    } catch (e) {
      // Bad JSON from the model — skip the chart silently rather than crash
      return null;
    }
  }, [chart_spec_json]);

  return (
    <Block icon="microscope" title="Deep analysis">
      {question && (
        <div style={{
          fontSize: 12, color: 'var(--fg-muted)', marginBottom: 10,
          fontStyle: 'italic',
        }}>{question}</div>
      )}
      {narrative && (
        <div className="q-md" style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg)' }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownTableComponents}>{narrative}</ReactMarkdown>
        </div>
      )}
      {table_md && (
        <div className="q-md" style={{
          marginTop: 12, fontSize: 13, color: 'var(--fg)',
          overflow: 'auto',
        }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownTableComponents}>{table_md}</ReactMarkdown>
        </div>
      )}
      {chartSpec && (
        <div style={{ marginTop: 14 }}>
          <Plot
            data={chartSpec.data || []}
            layout={{
              ...(chartSpec.layout || {}),
              autosize: true,
              height: 320,
              margin: { t: 28, r: 16, l: 48, b: 44 },
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
            }}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        </div>
      )}
      {code && (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setShowCode((s) => !s)}
            style={{
              fontSize: 12, color: 'var(--fg-muted)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 0, textDecoration: 'underline',
            }}
          >
            {showCode ? 'Hide code' : 'Show code'}
          </button>
          {showCode && (
            <pre style={{
              marginTop: 8, padding: 10, fontSize: 12, lineHeight: 1.5,
              background: 'var(--bg-elev-1)', border: '1px solid var(--line-soft)',
              borderRadius: 'var(--r-md)', overflow: 'auto',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              color: 'var(--fg)',
            }}>{code}</pre>
          )}
        </div>
      )}
    </Block>
  );
};

export default DeepAnalysisResult;
